/**
 * The console's local database.
 *
 * Everything the agent used to keep in `%LOCALAPPDATA%\CitariaApp` lives here
 * now: the operator's settings, their layouts and background images, the
 * directory handles that stand in for the configured folder paths, and the
 * upload queue that used to be a folder of JSON files.
 *
 * IndexedDB rather than localStorage because three of those four are binary or
 * structured — a `FileSystemDirectoryHandle` in particular is only persistable
 * by structured clone, which localStorage cannot do — and because the queue has
 * to survive a browser restart the same way the agent's `queue/*.json` survived
 * a machine restart.
 *
 * Hand-written rather than a dependency: this is four object stores and no
 * migrations beyond the first, and the console must keep working with the
 * venue's internet down, so every byte in the bundle is one an installed PWA
 * has to have cached correctly.
 */

const DB_NAME = 'citaria'
const DB_VERSION = 1

/** Settings, directory handles, the cached API token — one row per key. */
export const KV = 'kv'
/** Layouts, keyed by layout id. Replaces layouts.json. */
export const LAYOUTS = 'layouts'
/** Background images and previews, keyed by an opaque blob id. */
export const BLOBS = 'blobs'
/** Sessions waiting to reach raia-api. Replaces the agent's queue folder. */
export const QUEUE = 'queue'

let opening: Promise<IDBDatabase> | null = null

function open(): Promise<IDBDatabase> {
  if (opening) return opening

  opening = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(KV)) db.createObjectStore(KV)
      if (!db.objectStoreNames.contains(LAYOUTS)) db.createObjectStore(LAYOUTS, { keyPath: 'id' })
      if (!db.objectStoreNames.contains(BLOBS)) db.createObjectStore(BLOBS)
      if (!db.objectStoreNames.contains(QUEUE)) {
        db.createObjectStore(QUEUE, { keyPath: 'sessionKey' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('indexedDB.open failed'))
    // Another tab holding an old version open. The guest window is a second
    // tab, so this is reachable in normal use rather than theoretical.
    request.onblocked = () => reject(new Error('Database blocked by another tab'))
  })

  // A failed open must not be cached forever; the next call should retry.
  opening.catch(() => {
    opening = null
  })

  return opening
}

function run<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode)
        const request = fn(tx.objectStore(store))
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error ?? new Error(`${store} request failed`))
        tx.onabort = () => reject(tx.error ?? new Error(`${store} transaction aborted`))
      }),
  )
}

export function get<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  return run<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>)
}

export function getAll<T>(store: string): Promise<T[]> {
  return run<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>)
}

/** Stores with a keyPath take the value alone; `kv` and `blobs` take a key. */
export function put<T>(store: string, value: T, key?: IDBValidKey): Promise<IDBValidKey> {
  return run(store, 'readwrite', (s) => (key === undefined ? s.put(value) : s.put(value, key)))
}

export function del(store: string, key: IDBValidKey): Promise<undefined> {
  return run(store, 'readwrite', (s) => s.delete(key))
}

export function clear(store: string): Promise<undefined> {
  return run(store, 'readwrite', (s) => s.clear())
}

/** Replaces a keyPath store's whole contents in ONE transaction. */
export async function replaceAll<T>(store: string, values: T[]): Promise<void> {
  const db = await open()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite')
    const target = tx.objectStore(store)
    target.clear()
    for (const value of values) target.put(value)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error(`${store} replaceAll failed`))
    tx.onabort = () => reject(tx.error ?? new Error(`${store} replaceAll aborted`))
  })
}

/**
 * Asks the browser not to evict this origin's data.
 *
 * Without it, IndexedDB is "best effort" and Chrome may clear it under storage
 * pressure — which on an event PC would mean the operator's layouts and any
 * un-uploaded session disappearing mid-evening. Granted silently for an
 * installed PWA; a rejection is not fatal, so it is not treated as an error.
 */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false
  try {
    if (await navigator.storage.persisted()) return true
    return await navigator.storage.persist()
  } catch {
    return false
  }
}
